import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Heart, Search, User, Tag, Menu, X, Mail, Home } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/");
  };

  const pricingLabel = user?.role === "teacher" ? "Ücretler" : "Fiyatlandırma";

  // Build the same nav items used on desktop and mobile so parity is guaranteed
  const navItems = [];
  if (user && user.role === "family") {
    navItems.push(
      { to: "/ara", icon: Search, label: "Öğretmen Ara", testid: "nav-search" },
      { to: "/favorilerim", icon: Heart, label: "Favorilerim", testid: "nav-favorites" },
      { to: "/aile/profil", icon: User, label: "Profilim", testid: "nav-family-profile" },
    );
  }
  if (user && user.role === "teacher") {
    navItems.push(
      { to: "/ogretmen/profil", icon: User, label: "Profilim", testid: "nav-teacher-profile" },
    );
  }
  navItems.push(
    { to: "/iletisim", icon: Mail, label: "İletişim", testid: "nav-contact" },
    { to: "/fiyatlandirma", icon: Tag, label: pricingLabel, testid: "nav-pricing" },
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img
              src="/logo-tree.png"
              srcSet="/logo-tree.png 1x, /logo-tree@2x.png 2x"
              alt="Özel Ders Hocam"
              width="120"
              height="85"
              className="h-12 sm:h-12 w-auto object-contain [image-rendering:auto]"
              decoding="async"
              loading="eager"
            />
            <span className="font-display text-base sm:text-lg font-bold tracking-tight text-[color:var(--brand)]">Özel Ders Hocam</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map(({ to, icon: Icon, label, testid }) => (
              <Link key={to} to={to} data-testid={testid} className="text-sm text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1.5">
                <Icon size={16} /> {label}
              </Link>
            ))}
            {user ? (
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="rounded-full">
                <LogOut size={14} className="mr-1" /> Çıkış
              </Button>
            ) : (
              <Link to="/giris/aile" data-testid="nav-login">
                <Button size="sm" className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white">
                  Giriş Yap
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile hamburger toggle */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-slate-200 text-[color:var(--brand)] hover:bg-slate-50 active:scale-95 transition-all"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white shadow-lg" data-testid="mobile-menu">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              <Link
                to="/"
                data-testid="mobile-nav-home"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-[color:var(--brand-soft)] hover:text-[color:var(--brand)] transition-colors"
              >
                <Home size={18} /> <span className="font-medium">Ana Sayfa</span>
              </Link>
              {navItems.map(({ to, icon: Icon, label, testid }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`mobile-${testid}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-[color:var(--brand-soft)] hover:text-[color:var(--brand)] transition-colors"
                >
                  <Icon size={18} /> <span className="font-medium">{label}</span>
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-slate-100">
                {user ? (
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout-btn"
                    className="w-full flex items-center justify-center gap-2 h-11 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
                  >
                    <LogOut size={16} /> Çıkış Yap
                  </button>
                ) : (
                  <Link to="/giris/aile" data-testid="mobile-nav-login" className="block">
                    <button className="w-full flex items-center justify-center gap-2 h-11 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white font-medium">
                      Giriş Yap
                    </button>
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-neutral-200 mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid gap-10 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo-tree.png" alt="" className="h-10 w-auto" />
              <div className="font-display text-2xl font-semibold text-[color:var(--brand)]">Özel Ders Hocam</div>
            </div>
            <p className="mt-3 text-sm text-neutral-500 leading-relaxed max-w-xs">
              Türkiye'nin dört bir yanındaki nitelikli özel öğretmenlerle ailelerini buluşturuyoruz.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Menü</div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-700">
              <li><Link to="/" className="hover:text-neutral-900">Ana Sayfa</Link></li>
              <li><Link to="/fiyatlandirma" className="hover:text-neutral-900" data-testid="footer-pricing">Fiyatlandırma</Link></li>
              <li><Link to="/iletisim" className="hover:text-neutral-900" data-testid="footer-contact">İletişim</Link></li>
              <li><Link to="/giris/ogretmen" className="hover:text-neutral-900">Öğretmen Girişi</Link></li>
              <li><Link to="/giris/aile" className="hover:text-neutral-900">Aile Girişi</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Bilgi</div>
            <p className="mt-4 text-sm text-neutral-500 leading-relaxed">
              © {new Date().getFullYear()} Özel Ders Hocam. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
